import React, { useMemo, useState } from 'react';
import {
  useTable,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table';
import { type Alpaca } from '../models/Alpaca';
import { appTableFeatures, type AppTableFeatures } from './tableFeatures';
import PaginatedTable from './PaginatedTable';
import { format } from 'date-fns';
import MobileList from './MobileList';
import useMediaQuery from '../helpers/useMediaQuery';
import { Link } from 'react-router';

export default function AlpacaRosterTable({ alpacas }: { alpacas: Alpaca[] }) {
  const matches = useMediaQuery('screen and (max-width: 640px)');

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = React.useState('');

  const tableData = useMemo(() => alpacas, [alpacas]);

  const columnData: ColumnDef<AppTableFeatures, Alpaca>[] = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 300,
        cell: ({ row }) => (
          <Link
            prefetch='viewport'
            to={`/alpacas/${row.original.alpacaId}`}
            className='font-bold hover:opacity-70 dark:text-gray-300'
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        header: 'Status',
        accessorFn: (d) => (d.active ? 'Active' : 'Archived'),
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 100,
      },
      {
        header: 'Gender',
        accessorKey: 'gender.genderName',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 100,
      },
      {
        header: 'Breed',
        accessorKey: 'breed.breedName',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 100,
      },
      {
        header: 'Color',
        accessorKey: 'color.colorName',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 200,
      },
      {
        header: 'Birth',
        accessorFn: (d) =>
          d.dateOfBirth
            ? format(new Date(d.dateOfBirth ?? ''), 'MM/dd/yyyy')
            : '',
        sortFn: (a, b, _, desc = true) => {
          const aDate = new Date(a.original.dateOfBirth ?? '');
          const bDate = new Date(b.original.dateOfBirth ?? '');
          if (desc) {
            return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
          }
          return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
        },
        filterFn: 'includesString',
        size: 130,
        cell: ({ row }) =>
          row.original.dateOfBirth
            ? format(new Date(row.original.dateOfBirth ?? ''), 'MM/dd/yyyy')
            : '',
      },
      {
        header: 'Registry#',
        accessorKey: 'registryNumber',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 125,
      },
      {
        header: 'Tag#',
        accessorKey: 'tagNumber',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 100,
      },
      {
        header: 'Farm',
        accessorKey: 'farm.name',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 250,
        cell: ({ row }) =>
          row.original.farm ? (
            <Link
              to={`/farms/${row.original.farm.farmId}`}
              className='font-bold hover:opacity-70'
            >
              {row.original.farm.name}
            </Link>
          ) : (
            ''
          ),
      },
    ],
    [],
  );

  const columns = useMemo(() => columnData, [columnData]);

  const table = useTable({
    features: appTableFeatures,
    data: tableData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    defaultColumn: {
      size: 0,
      minSize: 0,
    },
    columnResizeMode: 'onChange',
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    paginateExpandedRows: false,
  });

  if (matches) {
    return (
      <MobileList
        table={table}
        columnResizeMode='onChange'
        showFilters={true}
      />
    );
  }
  return (
    <PaginatedTable
      table={table}
      columnResizeMode='onChange'
      showFilters={true}
    />
  );
}
